import Head from 'next/head';
import first from 'lodash/first';
import { useRouter, NextRouter } from 'next/router';

import { IArticle, IArticleIntroductionSection } from 'editorial/types';
import { getFirstPictureInSections } from 'editorial/utils/getFirstPictureInSections';
import { getNewsArticleSchema } from 'editorial/utils/getNewsArticleSchema';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { t } from 'localization/utils/t';

const TP = 'editorial.components.ArticleHead';

export const ArticleHead = ({ article }: { article: IArticle }) => {
  const { author, subject, description, sections, category, socialPreviewPicture } = article;
  const {
    query: { slug },
  }: NextRouter = useRouter();

  const introductionSection = first(sections) as IArticleIntroductionSection;
  const heroImage = getFirstPictureInSections(sections);
  const socialImage = socialPreviewPicture?.url;
  const isGreatest = category === 'greatest';
  const descriptionText = description || introductionSection?.teaser;
  const { pageTitle } = usePageMetadata({
    defaultTitle: `${subject} ${
      isGreatest ? t(`${TP}.interviewWithGreatest`, 'Interview With Greatest ') : ''
    }`,
  });
  return (
    <Head>
      <title key="title">{pageTitle}</title>
      <meta name="description" content={descriptionText} key="description" />
      <meta name="twitter:description" content={descriptionText} key="twitter:description" />
      <meta name="twitter:title" content={pageTitle} key="twitter:title" />
      <meta property="og:description" content={descriptionText} key="og:description" />
      <meta property="og:title" content={pageTitle} key="og:title" />
      <meta name="twitter:image" content={socialImage || heroImage} key="twitter:image" />
      <meta property="og:image" content={socialImage || heroImage} key="og:image" />
      <meta name="robots" content="max-image-preview:large" />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: getNewsArticleSchema(author, descriptionText, sections, slug, subject),
        }}
      />
    </Head>
  );
};
